import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MessageSquare, RefreshCw, CheckCircle2, AlertTriangle, XCircle, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import { fmtDateTime } from "@/lib/datetime";

function StatusPill({ status }) {
  const map = {
    healthy: { icon: CheckCircle2, label: "Healthy", cls: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30" },
    error: { icon: XCircle, label: "Error", cls: "text-red-400 bg-red-400/10 border-red-400/30" },
    not_configured: { icon: AlertTriangle, label: "Not configured", cls: "text-amber-400 bg-amber-400/10 border-amber-400/30" },
  };
  const m = map[status] || map.not_configured;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${m.cls}`}>
      <Icon size={13} /> {m.label}
    </span>
  );
}

function fmtTs(ts) {
  if (!ts) return "—";
  try { return fmtDateTime(ts); } catch { return ts; }
}

export default function NotificationsHealth({ token, onAuthError }) {
  const [health, setHealth] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testEmail, setTestEmail] = useState("");
  const [testPhone, setTestPhone] = useState("");
  const [sending, setSending] = useState("");

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    setLoading(true);
    try {
      const [h, r] = await Promise.all([
        axios.get(`${API}/notifications/health`, auth),
        axios.get(`${API}/notifications/recent?limit=15`, auth),
      ]);
      setHealth(h.data);
      setRecent(r.data || []);
    } catch (e) {
      if (e?.response?.status === 401 && onAuthError) onAuthError();
      else toast.error("Failed to load notification health");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const sendTest = async (channel) => {
    const to = channel === "email" ? testEmail.trim() : testPhone.trim();
    if (!to) { toast.error(`Enter a ${channel === "email" ? "email address" : "phone number"}`); return; }
    setSending(channel);
    try {
      const { data } = await axios.post(`${API}/notifications/test`, { channel, to }, auth);
      if (data.sent) toast.success(`Test ${channel} sent to ${to}`);
      else toast.error(`Test ${channel} was not sent`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to send test ${channel}`);
    } finally {
      setSending("");
    }
  };

  if (loading && !health) {
    return <div className="flex items-center gap-2 text-white/60 py-10"><Loader2 className="animate-spin" size={18} /> Loading notification health…</div>;
  }
  if (!health) return null;

  const e = health.email, s = health.sms;
  const quotaPct = e.daily_limit ? Math.min(100, Math.round((e.sent_today / e.daily_limit) * 100)) : 0;

  return (
    <div className="space-y-6" data-testid="notifications-health">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Notifications Health</h2>
          <p className="text-white/50 text-sm">Live status of email &amp; SMS delivery · checked {fmtTs(health.checked_at)}</p>
        </div>
        <button data-testid="notif-refresh-btn" onClick={load}
          className="flex items-center gap-2 px-4 py-2 rounded-md bg-[#0C0C0C] border border-white/10 text-white/80 hover:text-white text-sm font-semibold">
          <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {e.sandbox_sender && (
        <div data-testid="notif-sandbox-banner" className="flex items-start gap-3 p-4 rounded-lg bg-amber-500/10 border border-amber-500/40">
          <AlertTriangle className="text-amber-400 mt-0.5 shrink-0" size={18} />
          <div className="text-sm text-amber-200">
            <b>Email is using the Resend sandbox sender ({e.sender}).</b>
            <span className="block text-amber-200/80 mt-1">
              In this mode emails can only reach your own Resend account address — customers will NOT receive their booking/agreement emails.
              Fix it by setting <code className="bg-black/40 px-1.5 py-0.5 rounded text-amber-100">SENDER_EMAIL = Auto-Hire STL &lt;support@auto-hire.info&gt;</code> in your deployment's Environment Variables, then redeploy.
            </span>
          </div>
        </div>
      )}

      {health.failed_today > 0 && (
        <div data-testid="notif-failure-banner" className="flex items-start gap-3 p-4 rounded-lg bg-red-500/10 border border-red-500/30">
          <AlertTriangle className="text-red-400 mt-0.5" size={18} />
          <div className="text-sm text-red-200">
            <b>{health.failed_today}</b> notification{health.failed_today > 1 ? "s" : ""} failed today.
            {health.last_failure?.error && <span className="block text-red-300/70 mt-1">Last error: {health.last_failure.error}</span>}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-5">
        {/* Email card */}
        <div data-testid="notif-email-card" className="rounded-xl bg-[#0C0C0C] border border-white/10 p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-white font-semibold"><Mail size={18} className="text-[#F2B705]" /> Email (Resend)</div>
            <StatusPill status={e.status} />
          </div>
          <div className="space-y-1.5 text-sm text-white/70">
            <div className="flex justify-between"><span>Sender</span><span className="text-white/90 truncate ml-3">{e.sender || "—"}</span></div>
            <div className="flex justify-between"><span>Sent today</span><span className="text-white/90 font-semibold" data-testid="notif-email-sent-today">{e.sent_today}</span></div>
            <div className="flex justify-between"><span>Remaining quota</span><span className="text-white/90 font-semibold" data-testid="notif-email-quota">{e.quota_remaining} / {e.daily_limit}</span></div>
          </div>
          <div className="mt-3 h-2 rounded-full bg-white/10 overflow-hidden">
            <div className={`h-full ${quotaPct >= 90 ? "bg-red-500" : quotaPct >= 70 ? "bg-amber-400" : "bg-emerald-500"}`} style={{ width: `${quotaPct}%` }} />
          </div>
          <div className="mt-3 text-xs text-white/50">Last: {e.last ? `${e.last.status} · ${e.last.type || "email"} · ${fmtTs(e.last.ts)}` : "no emails sent yet"}</div>
          <div className="mt-4 flex gap-2">
            <input data-testid="notif-test-email-input" value={testEmail} onChange={(ev) => setTestEmail(ev.target.value)} placeholder="you@example.com"
              className="flex-1 px-3 py-2 rounded-md bg-black border border-white/15 text-white text-sm focus:border-[#F2B705] outline-none" />
            <button data-testid="notif-send-test-email-btn" onClick={() => sendTest("email")} disabled={sending === "email"}
              className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#F2B705] text-black text-sm font-semibold disabled:opacity-60">
              {sending === "email" ? <Loader2 className="animate-spin" size={15} /> : <Send size={15} />} Test
            </button>
          </div>
        </div>

        {/* SMS card */}
        <div data-testid="notif-sms-card" className="rounded-xl bg-[#0C0C0C] border border-white/10 p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-white font-semibold"><MessageSquare size={18} className="text-[#F2B705]" /> SMS (GoHighLevel)</div>
            <StatusPill status={s.status} />
          </div>
          <div className="space-y-1.5 text-sm text-white/70">
            <div className="flex justify-between"><span>Sent today</span><span className="text-white/90 font-semibold" data-testid="notif-sms-sent-today">{s.sent_today}</span></div>
            <div className="flex justify-between"><span>Configured</span><span className="text-white/90 font-semibold">{s.configured ? "Yes" : "No"}</span></div>
          </div>
          <div className="mt-3 text-xs text-white/50">Last: {s.last ? `${s.last.status} · ${fmtTs(s.last.ts)}` : "no texts sent yet"}</div>
          <div className="mt-4 flex gap-2">
            <input data-testid="notif-test-phone-input" value={testPhone} onChange={(ev) => setTestPhone(ev.target.value)} placeholder="+13145551234"
              className="flex-1 px-3 py-2 rounded-md bg-black border border-white/15 text-white text-sm focus:border-[#F2B705] outline-none" />
            <button data-testid="notif-send-test-sms-btn" onClick={() => sendTest("sms")} disabled={sending === "sms"}
              className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#F2B705] text-black text-sm font-semibold disabled:opacity-60">
              {sending === "sms" ? <Loader2 className="animate-spin" size={15} /> : <Send size={15} />} Test
            </button>
          </div>
        </div>
      </div>

      {/* Recent activity */}
      <div className="rounded-xl bg-[#0C0C0C] border border-white/10 p-5">
        <h3 className="text-white font-semibold mb-3">Recent Notifications</h3>
        {recent.length === 0 ? (
          <p className="text-white/40 text-sm">No notifications logged yet.</p>
        ) : (
          <div className="divide-y divide-white/5" data-testid="notif-recent-list">
            {recent.map((n, i) => (
              <div key={i} className="flex items-center justify-between py-2.5 text-sm">
                <div className="flex items-center gap-3 min-w-0">
                  {n.channel === "email" ? <Mail size={15} className="text-white/40 shrink-0" /> : <MessageSquare size={15} className="text-white/40 shrink-0" />}
                  <div className="min-w-0">
                    <div className="text-white/80 truncate">{n.type || n.channel}</div>
                    <div className="text-white/40 text-xs truncate">{n.to}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={`text-xs font-semibold ${n.status === "sent" ? "text-emerald-400" : n.status === "failed" ? "text-red-400" : "text-amber-400"}`}>{n.status}</span>
                  <span className="text-white/40 text-xs">{fmtTs(n.ts)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
